import React from 'react';

import { useTranslation } from 'react-i18next';

import { UNIT } from '@/enums/unit';

import styles from './styles.module.css';

interface TrendingValueProps {
  value: number | null;
  unit?: UNIT;
  secondaryValue?: number | null;
  secondaryUnit?: UNIT;
  secondaryValueParentheses?: boolean;
  verticalAlign?: boolean;
  variant?: 'dashboard' | 'pages-detail';
  icon?: React.ReactNode;
}

export const TrendingValue: React.FC<TrendingValueProps> = ({
  value,
  unit = UNIT.EURO,
  secondaryValue,
  secondaryUnit = UNIT.PERCENT,
  secondaryValueParentheses = true,
  verticalAlign = false,
  variant = 'pages-detail',
  icon,
}) => {
  const { t } = useTranslation();
  const getTrendClass = (): string => {
    const variantClass = variant === 'dashboard' ? styles.trendingValueDashboard : '';
    const baseClasses = [styles.trendingValue, variantClass].filter(Boolean).join(' ');

    if (value === null) {
      return `${baseClasses} ${styles.trendingValueNoData}`;
    } else if (value > 0) {
      return `${baseClasses} ${styles.trendingValuePositive}`;
    } else if (value < 0) {
      return `${baseClasses} ${styles.trendingValueNegative}`;
    }
    return baseClasses;
  };

  const formatValueWithUnit = (num: number, unitType: UNIT): string => {
    const absValue = Math.abs(num);

    switch (unitType) {
      case UNIT.EURO:
        return t('format.currencyFlexible', { value: absValue });
      case UNIT.PERCENT:
        return t('format.percentFlexible', { value: absValue });
      case UNIT.NONE:
      default:
        return t('format.numberWithDecimals', { value: absValue });
    }
  };

  const getSign = (num: number): string => {
    return num >= 0 ? `${t('trendingValue.positiveSign')} ` : `${t('trendingValue.negativeSign')} `;
  };

  const getControlIcon = (plusMoinsValue: number | null) => {
    if (typeof plusMoinsValue === 'number' && plusMoinsValue > 0) {
      return 'totem-icon totem-icon-growth-arrow';
    }
    if (typeof plusMoinsValue === 'number' && plusMoinsValue < 0) {
      return 'totem-icon totem-icon-decreasing-arrow';
    }
    return '';
  };

  const formatSecondaryValue = (parentheses: boolean, value: number, unitType: UNIT): string => {
    const secondaryFormatted = getSign(value) + formatValueWithUnit(value, unitType);
    return parentheses ? `(${secondaryFormatted})` : secondaryFormatted;
  };

  return (
    <div className={`${getTrendClass()} ${verticalAlign ? styles.verticalAlign : ''}`.trim()}>
      {icon && (
        <span className={styles.trendingValueIcon}>
          <i className={getControlIcon(value)} />
        </span>
      )}
      <span className={styles.trendingValueMain}>
        {value === null ? (
          t('trendingValue.unavailablePerformance')
        ) : (
          <>
            {getSign(value)}
            {formatValueWithUnit(value, unit)}
          </>
        )}
      </span>
      {secondaryValue !== null && secondaryValue !== undefined ? (
        <span className={styles.trendingValueSecondary}>
          {' '}
          {formatSecondaryValue(secondaryValueParentheses, secondaryValue, secondaryUnit)}
        </span>
      ) : null}
    </div>
  );
};
